import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ActionSidePanel from 'src/components/design-system/ActionSidePanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { MANAGE_SUPPLIERS_LAPTOP_MIN_WIDTH_PX } from 'src/constants/manageSuppliers';
import useConfirm from 'src/hooks/useConfirm';
import useWindowSize from 'src/hooks/useWindowSize';
import CreateOfflineSupplierForm from './CreateOfflineSupplierForm';
import { useCreateOfflineSupplierForm } from './useCreateOfflineSupplierForm';

const CREATE_SUPPLIER_PANEL_DESKTOP_WIDTH_PX = 900;
const CREATE_SUPPLIER_DISCARD_DIALOG_MAX_WIDTH_PX = 500;
const CREATE_SUPPLIER_DISCARD_DIALOG_GUTTER_PX = 24;

export interface CreateSupplierSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void | Promise<void>;
}

const CreateSupplierSidePanel: FC<CreateSupplierSidePanelProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { width: viewportWidth } = useWindowSize();

  const panelWidth = useMemo(() => {
    if (viewportWidth <= 0) {
      return `${CREATE_SUPPLIER_PANEL_DESKTOP_WIDTH_PX}px`;
    }

    if (viewportWidth >= MANAGE_SUPPLIERS_LAPTOP_MIN_WIDTH_PX) {
      return `${CREATE_SUPPLIER_PANEL_DESKTOP_WIDTH_PX}px`;
    }

    if (viewportWidth >= 768) {
      return `${viewportWidth}px`;
    }

    return '100%';
  }, [viewportWidth]);

  const discardConfirmDialogWidth = useMemo(() => {
    if (viewportWidth <= 0) {
      return `${CREATE_SUPPLIER_DISCARD_DIALOG_MAX_WIDTH_PX}px`;
    }

    return `${Math.min(
      CREATE_SUPPLIER_DISCARD_DIALOG_MAX_WIDTH_PX,
      viewportWidth - CREATE_SUPPLIER_DISCARD_DIALOG_GUTTER_PX
    )}px`;
  }, [viewportWidth]);

  const {
    values,
    errors,
    touched,
    isDirty,
    isValid,
    isSaving,
    requiredFields,
    setFieldValue,
    handleFieldBlur,
    handleSave,
  } = useCreateOfflineSupplierForm({
    isOpen,
    onSuccess: onSaveSuccess ?? onClose,
  });

  const confirmDiscardChanges = async (): Promise<boolean> => {
    if (!isDirty) {
      return true;
    }

    return confirm({
      title: t('Unsaved Changes'),
      content: t('You have unsaved changes. Do you want to discard them?'),
      cancelButtonText: t('Cancel'),
      confirmButtonText: t('Yes, Discard'),
      width: discardConfirmDialogWidth,
      className: 'supplier-discard-confirm-dialog',
    });
  };

  const handleClose = async () => {
    const shouldClose = await confirmDiscardChanges();
    if (shouldClose) {
      onClose();
    }
  };

  const saveDisabledTooltip = !isDirty ? (
    <PopUpContentWrapper type="Information" contents={[t('No changes made')]} />
  ) : undefined;

  return (
    <ActionSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={t('Create New Offline Supplier')}
      showTitleTooltip={false}
      onSave={handleSave}
      onCancel={handleClose}
      saveDisabled={!isValid || !isDirty}
      saveLoading={isSaving}
      cancelDisabled={isSaving}
      saveDisabledTooltip={saveDisabledTooltip}
      width={panelWidth}
      className="create-supplier-side-panel bg-[#F1F1F1]"
      contentClassName="create-supplier-side-panel__content overflow-y-auto"
    >
      <CreateOfflineSupplierForm
        values={values}
        errors={errors}
        touched={touched}
        requiredFields={requiredFields}
        onFieldChange={setFieldValue}
        onFieldBlur={handleFieldBlur}
      />
    </ActionSidePanel>
  );
};

export default CreateSupplierSidePanel;
